"use client";

import { useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";
import Image from "next/image";

import { LazyMotion, MotionDiv } from "@/components/animate";
import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

import {
  navPreviewItem,
  navPreviewMotionProps,
  NAV_PREVIEW_EASE_OUT,
} from "./nav-preview-motion";
import {
  NavFloatingCard,
  NavPreviewGradientBackground,
  NavPreviewShell,
} from "./nav-preview-shell";

/** Matches Figma blog preview screen (node 5584:27209). */
export function ResourcesBlogNavPreview() {
  const t = useTranslations("Nav.preview.blog");
  const reduceMotion = useReducedMotion();

  const articles = [
    {
      title: t("articles.one.title"),
      subtitle: t("articles.one.subtitle"),
      href: "/blog/what-is-growth-capital",
      thumb: "/images/nav/blog-preview-thumb-a.png",
      thumbSize: "size-[38px]" as const,
      position: "left-[21px] top-[92px] z-[2] w-[318px]",
      shadow: true,
      restingOpacity: 1,
    },
    {
      title: t("articles.two.title"),
      subtitle: t("articles.two.subtitle"),
      href: "/blog/revenue-based-finance",
      thumb: "/images/nav/blog-preview-thumb-b.png",
      thumbSize: "size-8" as const,
      position: "left-[33px] top-[156px] z-[1] w-[306px]",
      shadow: false,
      restingOpacity: 0.4,
    },
  ] as const;

  const motionProps = navPreviewMotionProps(reduceMotion);

  return (
    <NavPreviewShell>
      <NavPreviewGradientBackground variant="purple" />
      <NavFloatingCard>
        <LazyMotion>
          <MotionDiv className="relative h-full w-full" {...motionProps}>
            <MotionDiv
              className="absolute top-[25px] left-[29px] flex w-[255px] flex-col gap-2"
              variants={reduceMotion ? undefined : navPreviewItem}
            >
              <p className="text-ds-body-lg text-grey-950 leading-[1.3] tracking-[-0.36px]">
                {t("featuredTitle")}
              </p>
              <div className="flex flex-wrap gap-2">
                <PreviewTag label={t("tags.finance")} tone="pulse" />
                <PreviewTag label={t("tags.insights")} tone="green" />
              </div>
            </MotionDiv>

            {articles.map((article) => (
              <MotionDiv
                key={article.href}
                className={cn("absolute", article.position)}
                variants={
                  reduceMotion
                    ? undefined
                    : {
                        hidden: { opacity: 0, y: 20 },
                        show: {
                          opacity: article.restingOpacity,
                          y: 0,
                          transition: {
                            duration: 0.48,
                            ease: NAV_PREVIEW_EASE_OUT,
                          },
                        },
                      }
                }
              >
                <Link
                  href={article.href}
                  className={cn(
                    "border-grey-100 bg-grey-50 rounded-ds-l flex items-center gap-3 border p-2",
                    article.shadow &&
                      "shadow-[2px_8px_19px_rgba(150,150,150,0.1)]",
                    reduceMotion && article.restingOpacity < 1 && "opacity-40"
                  )}
                >
                  <div
                    className={cn(
                      "border-grey-0 rounded-ds-s relative shrink-0 overflow-hidden border-2",
                      article.thumbSize
                    )}
                  >
                    <Image
                      src={article.thumb}
                      alt=""
                      fill
                      className="object-cover"
                      sizes="38px"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-grey-950 text-ds-body-xs line-clamp-1 tracking-[-0.24px]">
                      {article.title}
                    </p>
                    <p className="text-grey-600 line-clamp-1 text-[10px] leading-[1.4]">
                      {article.subtitle}
                    </p>
                  </div>
                </Link>
              </MotionDiv>
            ))}
          </MotionDiv>
        </LazyMotion>
      </NavFloatingCard>
    </NavPreviewShell>
  );
}

function PreviewTag({
  label,
  tone,
}: {
  label: string;
  tone: "pulse" | "green";
}) {
  return (
    <span
      className={cn(
        "text-grey-950 rounded-ds-full text-ds-label-sm inline-flex px-2.5 py-0.5 font-medium tracking-[0.02em] uppercase",
        tone === "pulse" && "bg-pulse-100",
        tone === "green" && "bg-green-100"
      )}
    >
      {label}
    </span>
  );
}
